"use client";
import { jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchFormAutocompleteConfig } from "../../context/SearchFormAutocompleteContext.js";
import { useCurrentLocation } from "../../hooks/useCurrentLocation.js";
import { useTypeAheadFeedback } from "../../hooks/useTypeAheadFeedback.js";
import { useTypeAheadSearch } from "../../hooks/useTypeAheadSearch.js";
import { createSearchOriginId } from "../../utils/create-search-origin-id.js";
import { AutocompleteSearchField } from "./AutocompleteSearchField.js";
const EMPTY_LOCATIONS = [];
const ConnectedAutocompleteSearchField_ConnectedAutocompleteSearchField = /*#__PURE__*/ forwardRef(function({ typeAheadEndpoint = 'hotels', debounceTime, airportNestingEnabled = false, neighbourhoodNestingEnabled = false, hasAISearch = false, hasLTRSearch = false, isMobile = false, onFilterChipToggle, enableSearchOriginId = true, searchOriginResetGeneration = 0, onRegisterFinalizeFeedback, onItemSelect: onItemSelectProp, ...rest }, ref) {
    const config = useSearchFormAutocompleteConfig();
    const [searchValue, setSearchValue] = useState(null);
    const [searchOriginID, setSearchOriginID] = useState();
    const prevSearchOriginResetGenerationRef = useRef(searchOriginResetGeneration);
    const lastFeedbackPayloadRef = useRef(null);
    const isHotelEndpoint = 'hotels' === typeAheadEndpoint || 'hotelsV2' === typeAheadEndpoint;
    const shouldUseSearchOrigin = isHotelEndpoint && enableSearchOriginId;
    const sendFeedback = useTypeAheadFeedback(config.typeahead);
    const flushFinalizeFeedback = useCallback(()=>{
        const clickPayload = lastFeedbackPayloadRef.current;
        if (!clickPayload) return;
        lastFeedbackPayloadRef.current = null;
        sendFeedback({
            ...clickPayload,
            fetchedItems: [
                clickPayload.item
            ],
            eventType: 'finalize'
        });
    }, [
        sendFeedback
    ]);
    useEffect(()=>{
        if (!onRegisterFinalizeFeedback) return;
        onRegisterFinalizeFeedback(flushFinalizeFeedback);
        return ()=>{
            onRegisterFinalizeFeedback(null);
        };
    }, [
        onRegisterFinalizeFeedback,
        flushFinalizeFeedback
    ]);
    useEffect(()=>{
        if (prevSearchOriginResetGenerationRef.current === searchOriginResetGeneration) return;
        prevSearchOriginResetGenerationRef.current = searchOriginResetGeneration;
        flushFinalizeFeedback();
        setSearchOriginID(void 0);
    }, [
        searchOriginResetGeneration,
        flushFinalizeFeedback
    ]);
    const ensureSearchOriginID = useCallback(()=>{
        if (!shouldUseSearchOrigin) return;
        setSearchOriginID((current)=>current ?? createSearchOriginId());
    }, [
        shouldUseSearchOrigin
    ]);
    const searchKey = rest.searchKey;
    const persistedRecents = useMemo(()=>config.recentSearches?.getPersisted?.({
            searchKey,
            typeAheadEndpoint
        }) ?? EMPTY_LOCATIONS, [
        config.recentSearches,
        searchKey,
        typeAheadEndpoint
    ]);
    const recommendedItems = isHotelEndpoint ? config.recommendedSearches?.items ?? EMPTY_LOCATIONS : EMPTY_LOCATIONS;
    const typeAheadResult = useTypeAheadSearch({
        searchValue,
        typeAheadEndpoint,
        config: config.typeahead,
        debounceTime,
        airportNestingEnabled,
        neighbourhoodNestingEnabled,
        hasAISearch,
        hasLTRSearch,
        isMobile,
        searchOriginID: shouldUseSearchOrigin ? searchOriginID : void 0,
        onLtrSearch: config.analytics?.onLtrSearch,
        onResultCity: config.analytics?.onResultCity
    });
    const geolocation = useCurrentLocation({
        nearestCityBaseUrl: config.geolocation?.nearestCityBaseUrl,
        onError: config.analytics?.onError
    });
    const searchReference = shouldUseSearchOrigin ? typeAheadResult.meta?.searchReference : void 0;
    const onTypeaheadFeedback = useCallback((payload)=>{
        const nextPayload = {
            ...payload,
            typeAheadEndpoint: payload.typeAheadEndpoint,
            ...shouldUseSearchOrigin && {
                eventType: 'click'
            }
        };
        lastFeedbackPayloadRef.current = shouldUseSearchOrigin && !payload.isRecentOrRecommendedSearch && payload.item.entered ? nextPayload : null;
        sendFeedback(nextPayload);
    }, [
        sendFeedback,
        shouldUseSearchOrigin
    ]);
    const handleItemSelect = useCallback((item, context)=>{
        if (!item?.entered || context?.isPrefill) lastFeedbackPayloadRef.current = null;
        onItemSelectProp?.(item, context);
    }, [
        onItemSelectProp
    ]);
    const adapter = useMemo(()=>({
            session: {
                clientGUID: config.session?.clientGUID ?? '',
                appName: config.session?.appName ?? '',
                appVersion: config.session?.appVersion ?? '',
                signedIn: config.session?.signedIn ?? false
            },
            recentSearches: {
                local: persistedRecents,
                fetched: config.recentSearches?.fetched ?? EMPTY_LOCATIONS,
                isFetchedReady: config.recentSearches?.isFetchedReady ?? true,
                abandoned: config.recentSearches?.abandoned ?? EMPTY_LOCATIONS,
                isAbandonedReady: config.recentSearches?.isAbandonedReady ?? true,
                viewed: config.recentSearches?.viewed ?? EMPTY_LOCATIONS,
                isViewedReady: config.recentSearches?.isViewedReady ?? true
            },
            recommendedSearches: {
                items: recommendedItems
            },
            geolocation,
            typeAheadResult: {
                items: typeAheadResult.items,
                duration: typeAheadResult.duration,
                source: typeAheadResult.source,
                searchReference
            },
            onTypeaheadFeedback,
            onTypeaheadClick: config.analytics?.onTypeaheadClick,
            onAutocompleteOpen: config.analytics?.onAutocompleteOpen,
            onFilterChipToggle: onFilterChipToggle ?? config.analytics?.onFilterChipToggle,
            onAiSearchClick: config.analytics?.onAiSearchClick,
            resolveItemFirstLineSuffix: config.resolveItemFirstLineSuffix ? (item)=>config.resolveItemFirstLineSuffix?.(item, {
                    typeAheadEndpoint
                }) : void 0,
            resolveFilterChipData: config.resolveFilterChipData
        }), [
        config,
        geolocation,
        typeAheadResult.items,
        typeAheadResult.duration,
        typeAheadResult.source,
        searchReference,
        onTypeaheadFeedback,
        onFilterChipToggle,
        persistedRecents,
        recommendedItems,
        typeAheadEndpoint
    ]);
    return /*#__PURE__*/ jsx(AutocompleteSearchField, {
        ...rest,
        ref: ref,
        typeAheadEndpoint: typeAheadEndpoint,
        searchValue: searchValue,
        onSearchValueChange: setSearchValue,
        onItemSelect: handleItemSelect,
        airportNestingEnabled: airportNestingEnabled,
        neighbourhoodNestingEnabled: neighbourhoodNestingEnabled,
        isMobile: isMobile,
        hasAISearch: hasAISearch,
        hasLTRSearch: hasLTRSearch,
        onMenuOpen: ensureSearchOriginID,
        adapter: adapter
    });
});
export { ConnectedAutocompleteSearchField_ConnectedAutocompleteSearchField as ConnectedAutocompleteSearchField };
